<template>
    <div class="">
        <div class="d-flex align-items-baseline justify-content-between mb-3">
            <h4 class="mb-0 text-primary-darker">Quote Sheet Templates</h4>
            <b-button variant="success" @click="handleCreateClick">
                <b-icon icon="plus-circle"></b-icon>
                New Template
            </b-button>
        </div>
        <div
            v-if="!hasTemplates"
            key="noTemplatesOrLoading"
            class="w-100 d-flex align-items-center justify-content-center border rounded-lg bg-gray-200 overflow-hidden"
            style="min-height: 150px;"
        >
            <div class="text-center text-muted special-text">
                {{ loadingTemplates ? 'Loading...' : 'No Templates' }}
            </div>
        </div>
        <b-list-group
            v-else
            key="templateList"
            class="shadow-sm"
        >
            <b-list-group-item
                v-for="tpl in templates"
                :key="tpl.id"
            >
                <div class="row">
                    <div class="col-4 d-flex align-items-center justify-content-start">
                        <h5 class="mb-0">{{ tpl.title }}</h5>
                    </div>
                    <div class="col-3 d-flex align-items-center justify-content-center">
                        <span class="text-muted">
                            <span class="special-text font-size-70">Created:</span>
                            {{ formatDate(tpl.created) }}
                        </span>
                    </div>
                    <div class="col-3 d-flex align-items-center justify-content-center">
                        <b-badge>{{tpl.currency}}</b-badge>
                    </div>
                    <div class="col-2 d-flex align-items-center justify-content-end">
                        <b-button variant="minimal-danger" class="mr-2" @click="handleDeleteClick(tpl)">
                            <b-icon icon="trash"></b-icon>
                        </b-button>
                        <b-button variant="minimal-secondary" class="mr-2 special-text" @click="handleCloneClick(tpl)">
                            Clone
                        </b-button>
                        <b-button variant="light-primary" class="special-text" @click="startEdit(tpl)">
                            Edit
                        </b-button>
                    </div>
                </div>
            </b-list-group-item>
        </b-list-group>

        <!-- builder modal -->
        <b-modal
            v-model="builderShowing"
            hide-footer
            size="full"
            centered
            title="Quote Sheet Builder"
            body-class="d-flex flex-column bg-gray-300 p-0"
            hide-header-close
            no-close-on-esc
            no-close-on-backdrop
            @hidden="refreshList"
        >
            <quote-sheet-builder
                :quote-sheet-id="editQsId"
                @request-close="closeModal"
            ></quote-sheet-builder>
        </b-modal>

    </div>
</template>

<script>
import { errorHandling, tenantInfoMixin } from "../../js/custom-vue-mixins";
import { suppliosApi } from "../../js/api-service";
import { getDateTimeSlug, FormatDateTimeLocal } from "../../js/filters-helpers";

export default {
    name: "QuoteSheetTemplateManager",
    components: {},
    mixins: [errorHandling, tenantInfoMixin],
    props: {
    },
    data() {
        return {
            loadingTemplates: false,
            templates: [],
            loadingCreate: false,
            builderShowing: false,
            editQsId: '',
            loadingDelete: false,
        };
    },
    computed: {
        hasTemplates() {
            return !!this.templates?.length;
        },
    },
    mounted() {
        this.getTemplates();
    },
    methods: {
        async getTemplates() {
            this.loadingTemplates = true;
            try {
                const { data } = await suppliosApi.quoteSheet.list_templates();
                this.templates = data ? [...data] : [];
            } catch (error) {
                this.apiErrorHandler(error);
            }
            this.loadingTemplates = false;
        },
        async refreshList() {
            return await this.getTemplates();
        },
        async handleCreateClick() {
            const defaultTitle = 'Quote Sheet Template';
            return await this.performCreate({title: defaultTitle});
        },
        async handleCloneClick(cloneObj) {
            // make a clone from existing template!  we can use same endpoint, just send the 'quotesheet_template_id' along for the one you want to clone 
            const cloneId = cloneObj.id;
            const newTitle = `${cloneObj.title} COPY`; 
            return await this.performCreate({quotesheet_template_id: cloneId, title: newTitle});
        },
        async performCreate(createData) {
            this.loadingCreate = true;
            // create a new template, no data captured from user FOR NOW, unless they want to clone one - they can modify once we create it
            // but we MUST pass a 'template_slug' (unique per tenant) to identify that this is a TEMPLATE, not a normal quotesheet
            // we'll use a specific format to avoid collisions
            const postData = createData ? {...createData} : {};
            if (!postData.template_slug) {
                // if you didn't pass it in (likely no, for now at least) then make a new slug
                postData['template_slug'] = `template_${getDateTimeSlug()}`;  // like 'template_20230124091555'
            }
            let newQuoteSheet;
            try {
                const { data } = await suppliosApi.quoteSheet.create(postData);
                console.log('new quotesheet created', data);
                newQuoteSheet = data;  // just to see if we got anything
            } catch (error) {
                this.apiErrorHandler(error);
            }
            if (newQuoteSheet) {
                await this.refreshList();  // reload instead of patching the local stash
            }
            this.loadingCreate = false;
            // must have loadingCreate false before opening edit modal, or else it won't open 
            if (newQuoteSheet) {
                this.startEdit(newQuoteSheet);
            }
        },
        formatDate(val) {
            return FormatDateTimeLocal(val);
        },
        startEdit(templateObj) {
            // open the edit modal 
            const templateId = templateObj.id;
            console.log('edit', templateId);
            if (this.builderShowing || this.loadingTemplates || this.loadingCreate) {
                return;  
            }
            this.editQsId = templateId;
            this.builderShowing = true;
        },
        closeModal() {
            this.builderShowing = false;  
            this.editQsId = '';  // reset this
        },
        async handleDeleteClick(templateObj) {
            const templateId = templateObj.id;
            console.log('delete', templateId);
            const isConfirmed = await this.$bvModal.msgBoxConfirm('Are you sure you want to delete this template?  None of the Quote Sheets created with it (if any) will be affected.', {
                title: 'Please Confirm',
                size: 'sm',
                buttonSize: 'sm',
                okVariant: 'danger',
                okTitle: 'Yes, Delete',
                cancelTitle: 'No, Cancel',
                footerClass: 'p-2',
                hideHeaderClose: false,
                centered: true
            });
            if (isConfirmed) {
                this.performDelete(templateId);  // just trigger it and go
            }
        },
        async performDelete(templateId) {
            this.loadingDelete = true;
            //let resp;
            try {
                await suppliosApi.quoteSheet.destroy(templateId);
            } catch (error) {
                this.apiErrorHandler(error);
            }
            this.refreshList();  // just trigger it and go
            this.loadingDelete = false;
        },
    }
}
</script>

<style>

</style>